<template>
    <div>
        <transition name="page">
            <v-container class="pa-5" fluid>
                <SaveMachineDialog
                    :machine-data="currentMachine"
                    :machine-print-types="machinePrintTypes"
                    :machine-sheet-formats="machineSheetFormats"
                    :machine-plate-types="machinePlateTypes"
                    :production-departments="productionDepartments"
                    :production-processes="productionProcesses"
                    :machine-dur-groups="machineDurGroups"
                    :is-open="saveMachineDialog"
                    @on-confirm="confirmSaveMachineDialog"
                    @on-close="closeSaveMachineDialog"
                />

                <MachineProcessCostDialog
                    :machine-data="currentMachine"
                    :is-open="machineProcessCostDialog"
                    @show-machine-process-dialog="showMachineProcessCostDialog"
                    @on-close="closeMachineProcessCostDialog"
                />

                <EfficiencyConfigDialog
                    :machine-data="currentMachine"
                    :is-open="efficiencyConfigDialog"
                    @on-success="handleEfficiencyConfigSave"
                    @on-close="closeEfficiencyConfigDialog"
                />

                <DigitalRollEfficiencyDialog
                    :machine-data="currentMachine"
                    :is-open="digitalRollEfficiencyDialog"
                    @on-success="handleDigitalRollEfficiencySave"
                    @on-close="closeDigitalRollEfficiencyDialog"
                    @on-papers-loaded="handlePapersLoaded"
                />

                <page-title>Lista maszyn</page-title>

                <v-row align="center">
                    <v-col cols="12" lg="4">
                        <v-btn
                            color="secondary"
                            medium
                            data-sentry-label="Dodaj maszynę"
                            :loading="actionLoading"
                            @click="showCreateMachineDialog"
                        >
                            <v-icon left>
                                mdi-plus
                            </v-icon>
                            Dodaj maszynę
                        </v-btn>
                    </v-col>
                    <v-col cols="12" lg="8">
                        <v-container fluid class="pa-0">
                            <v-row justify="end" dense>
                                <v-col sm="auto">
                                    <v-checkbox
                                        v-model="filters.isVirtual"
                                        label="Ukryj wirtualne"
                                        hide-details
                                        class="mt-2"
                                        @change="changeFilter"
                                    />
                                </v-col>
                                <v-col cols="12" sm="8" md="6" lg="4" xl="3">
                                    <v-select
                                        v-model="filters.productionDepartment_id"
                                        :items="productionDepartments"
                                        item-value="id"
                                        item-text="name"
                                        placeholder="Działy..."
                                        no-data-text="Brak danych..."
                                        hide-details
                                        solo
                                        clearable
                                        @change="changeFilter"
                                    />
                                </v-col>
                                <v-col cols="12" sm="8" md="6" lg="4" xl="3">
                                    <v-text-field
                                        v-model="filters.name"
                                        append-icon="mdi-magnify"
                                        label="Szukaj..."
                                        single-line
                                        clearable
                                        hide-details
                                        solo
                                        @keyup.enter="changeFilter"
                                        @click:append="changeFilter"
                                        @click:clear="clearFilter('name')"
                                    />
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-card>
                            <v-data-table
                                :headers="headers"
                                :items="machines"
                                :loading="machinesLoading"
                                :footer-props="footerProps"
                                :server-items-length="pagination.itemsLength"
                                :mobile-breakpoint="0"
                                :page.sync="pagination.page"
                                name="machinesData"
                                @pagination="updatePagination"
                            >
                                <template #item.name="{item}">
                                    {{ item.name }}
                                </template>
                                <template #item.symbol="{item}">
                                    {{ item.symbol }}
                                </template>
                                <template #item.numberOfAggregate="{item}">
                                    {{ parseValue(item.numberOfAggregate) }}
                                </template>
                                <template #item.productionDepartment="{item}">
                                    {{ item.productionDepartment && item.productionDepartment.name }}
                                </template>
                                <template #item.canReportAccident="{item}">
                                    <v-icon v-if="item.canReportAccident" small color="success">
                                        mdi-check
                                    </v-icon>
                                    <v-icon v-else small color="error">
                                        mdi-close
                                    </v-icon>
                                </template>
                                <template #item.isVisible="{item}">
                                    <v-icon v-if="item.isVisible" small color="success">
                                        mdi-check
                                    </v-icon>
                                    <v-icon v-else small color="error">
                                        mdi-close
                                    </v-icon>
                                </template>
                                <template #item.actions="{item}">
                                    <div class="d-inline-flex">
                                        <v-tooltip top :open-delay="tooltipDelay">
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Konfiguracja wydajności"
                                                    :loading="efficiencyConfigLoadingId === item.id || digitalRollEfficiencyLoadingId === item.id"
                                                    :disabled="efficiencyConfigLoadingId === item.id || digitalRollEfficiencyLoadingId === item.id"
                                                    v-on="on"
                                                    @click="showEfficiencyConfigDialog(item.id)"
                                                >
                                                    <v-icon color="purple">
                                                        mdi-speedometer
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Konfiguracja wydajności</span>
                                        </v-tooltip>
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Edytuj"
                                                    v-on="on"
                                                    @click="showEditMachineDialog(item.id)"
                                                >
                                                    <v-icon color="teal">
                                                        mdi-pencil
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Edycja</span>
                                        </v-tooltip>
                                        <v-tooltip v-if="canUpdatePriceCost" top :open-delay="tooltipDelay">
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Dodaj koszt maszyny"
                                                    v-on="on"
                                                    @click="showMachineProcessCostDialog(item.id)"
                                                >
                                                    <v-icon color="info">
                                                        mdi-currency-usd
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Koszt maszyny</span>
                                        </v-tooltip>
                                    </div>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </transition>
    </div>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import SaveMachineDialog from './SaveMachineDialog'
import MachineProcessCostDialog from './MachineProcessCostDialog'
import EfficiencyConfigDialog from './EfficiencyConfigDialog'
import DigitalRollEfficiencyDialog from './DigitalRollEfficiencyDialog'

import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import { appConfig } from '@/config'

import { errorHandler } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'Machines',
    components: {
        PageTitle,
        SaveMachineDialog,
        MachineProcessCostDialog,
        EfficiencyConfigDialog,
        DigitalRollEfficiencyDialog
    },
    mixins: [
        filters,
        pagination,
        tooltipDelay,
    ],
    beforeRouteLeave(to, from, next) {
        this.$store.dispatch('configurations/clearMachinesFilters')
        next()
    },
    data() {
        return {
            currentMachine: {},
            machinePrintTypes: [],
            machineSheetFormats: [],
            machinePlateTypes: [],
            machineDurGroups: [],
            saveMachineDialog: false,
            actionLoading: false,
            machineProcessCostDialog: false,
            efficiencyConfigDialog: false,
            efficiencyConfigLoadingId: null,
            digitalRollEfficiencyDialog: false,
            digitalRollEfficiencyLoadingId: null,
            headers: [
                {
                    text: 'Nazwa maszyny',
                    value: 'name',
                    sortable: false
                },
                {
                    text: 'Symbol',
                    value: 'symbol',
                    sortable: false
                },
                {
                    text: 'Ilość agregatów',
                    value: 'numberOfAggregate',
                    sortable: false
                },
                {
                    text: 'Dział',
                    value: 'productionDepartment',
                    sortable: false
                },
                {
                    text: 'Zgłaszanie awarii',
                    value: 'canReportAccident',
                    sortable: false,
                    align: 'center',
                    width: 140
                },
                {
                    text: 'Czy widoczna?',
                    value: 'isVisible',
                    sortable: false,
                    align: 'center',
                    width: 120
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        machines() {
            return this.$store.state.configurations.machines.machines
        },
        machinesLoading() {
            return this.$store.state.configurations.machines.machinesLoading
        },
        productionDepartments() {
            return this.$store.state.administration.users.productionDepartments
        },
        productionProcesses() {
            return this.$store.state.configurations.productProductionProcesses.productionProcesses
        },
        savedFilters() {
            return this.$store.state.configurations.machines.filters
        },
        canUpdatePriceCost() {
            return hasUserPermission('machines_process_cost_update')
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    if (item === 'isVirtual' && filters[item] === true) {
                        filters[item] = false
                    }
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    mounted() {
        this.getMachines()
        this.getProductionDepartments()
    },
    methods: {
        showMachineProcessCostDialog(id) {
            this.$store.dispatch('configurations/getMachine', id)
                .then(response => {
                    this.currentMachine = response.data
                    this.machineProcessCostDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        closeMachineProcessCostDialog() {
            this.machineProcessCostDialog = false
        },
        getMachines() {
            this.$store.dispatch('configurations/getMachines', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getProductionDepartments() {
            this.$store.dispatch('administration/getProductionDepartments')
                .catch(error => {
                    errorHandler(error)
                })
        },
        showCreateMachineDialog() {
            this.actionLoading = true

            Promise.all([
                this.$store.dispatch('configurations/getMachinePrintTypes'),
                this.$store.dispatch('configurations/getMachineSheetFormats'),
                this.$store.dispatch('configurations/getMachinePlateTypes'),
                this.$store.dispatch('administration/getProductionDepartments'),
                this.$store.dispatch('configurations/getProductionProcesses', { paginate: '', query: '?isVirtual=false' }),
                this.$store.dispatch('configurations/getMachineDurGroups')
            ])
                .then(responses => {
                    this.currentMachine = {}
                    this.machinePrintTypes = responses[0].data.results
                    this.machineSheetFormats = responses[1].data.results
                    this.machinePlateTypes = responses[2].data.results
                    this.machineDurGroups = responses[5].data.results
                    this.saveMachineDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                })
        },
        showEditMachineDialog(machineId) {
            Promise.all([
                this.$store.dispatch('configurations/getMachine', machineId),
                this.$store.dispatch('configurations/getMachinePrintTypes'),
                this.$store.dispatch('configurations/getMachineSheetFormats'),
                this.$store.dispatch('configurations/getMachinePlateTypes'),
                this.$store.dispatch('administration/getProductionDepartments'),
                this.$store.dispatch('configurations/getProductionProcesses', { paginate: '', query: '?isVirtual=false' }),
                this.$store.dispatch('configurations/getMachineDurGroups')
            ])
                .then(responses => {
                    this.currentMachine = responses[0].data
                    this.machinePrintTypes = responses[1].data.results
                    this.machineSheetFormats = responses[2].data.results
                    this.machinePlateTypes = responses[3].data.results
                    this.machineDurGroups = responses[6].data.results
                    this.saveMachineDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        confirmSaveMachineDialog(action) {
            if (action === 'createMachine') {
                this.getMachines()
            }
        },
        closeSaveMachineDialog() {
            setTimeout(() => {
                this.currentMachine = {}
            }, 500)
            this.saveMachineDialog = false
        },
        parseValue(value) {
            return value ? value : '-'
        },
        changeFilter() {
            this.$store.dispatch('configurations/setMachinesFilters', this.filters)
            this.resetPagination()
            this.getMachines()
        },
        updatePagination(pagination) {
            this.pagination = pagination

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getMachines()
            }
            this.clearPaginationPending()
        },
        async showEfficiencyConfigDialog(id) {
            this.currentMachine = {}
            this.efficiencyConfigDialog = false
            this.digitalRollEfficiencyDialog = false
            this.efficiencyConfigLoadingId = id
            this.digitalRollEfficiencyLoadingId = id

            try {
                const machineResponse = await this.$store.dispatch('configurations/getMachine', id)

                this.currentMachine = machineResponse.data

                if (this.currentMachine.efficiencyTypeId === appConfig.configurations.machines.efficiencies.cuttingFromRollTypeId) {
                    this.$nextTick(() => {
                        this.efficiencyConfigDialog = true
                    })
                    this.efficiencyConfigLoadingId = null
                    this.digitalRollEfficiencyLoadingId = null
                } else if (this.currentMachine.isPrintingFromRoll) {
                    this.$nextTick(() => {
                        this.digitalRollEfficiencyDialog = true
                    })
                    this.efficiencyConfigLoadingId = null
                    this.digitalRollEfficiencyLoadingId = null
                } else {
                    const promises = []

                    if (this.currentMachine.efficiencyTypeId === 1) {
                        promises.push(
                            this.$store.dispatch('configurations/getPaperGroups', {
                                paginate: '',
                                query: '?isVisible=true'
                            })
                        )
                    }

                    if (promises.length > 0) {
                        await Promise.all(promises)
                    }

                    this.$nextTick(() => {
                        this.efficiencyConfigDialog = true
                    })
                    this.efficiencyConfigLoadingId = null
                    this.digitalRollEfficiencyLoadingId = null
                }
            } catch (error) {
                errorHandler(error)
                this.efficiencyConfigLoadingId = null
                this.digitalRollEfficiencyLoadingId = null
            }
        },
        closeEfficiencyConfigDialog() {
            this.efficiencyConfigDialog = false
            this.currentMachine = {}
        },
        handleEfficiencyConfigSave() {
            this.getMachines()
        },
        async showDigitalRollEfficiencyDialog(id) {
            this.currentMachine = {}
            this.digitalRollEfficiencyDialog = false
            this.rollCuttingFromRollEfficiencyDialog = false
            this.digitalRollEfficiencyLoadingId = id

            try {
                const machineResponse = await this.$store.dispatch('configurations/getMachine', id)

                this.currentMachine = machineResponse.data

                this.$nextTick(() => {
                    this.digitalRollEfficiencyDialog = true
                })
            } catch (error) {
                errorHandler(error)
                this.digitalRollEfficiencyLoadingId = null
            }
        },
        closeDigitalRollEfficiencyDialog() {
            this.digitalRollEfficiencyDialog = false
            this.currentMachine = {}
            this.digitalRollEfficiencyLoadingId = null
        },
        handleDigitalRollEfficiencySave() {
            this.getMachines()
        },
        handlePapersLoaded() {
            this.digitalRollEfficiencyLoadingId = null
        }
    }
}

</script>
